import { Button } from "@/components/ui/button";
import { useGameRequestStatus } from "@/hooks/useGameRequestStatus";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getPlaytimeForGame } from "@/services/steamPlaytimeService";
import { validateSteamAccount, SteamValidationResult } from "@/services/steamService";
import { Key, Clock, CheckCircle, XCircle, Package, AlertCircle, Info } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";

interface GameRequestButtonProps {
  gameId: string;
  gameTitle: string;
  allowRequests: boolean;
  availableKeys: number;
  onRequestClick: () => void;
  className?: string;
}

export const GameRequestButton = ({
  gameId,
  gameTitle,
  allowRequests,
  availableKeys,
  onRequestClick,
  className = ""
}: GameRequestButtonProps) => {
  const { requestStatus, loading: requestStatusLoading } = useGameRequestStatus(gameId);
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const [isOwnedOnSteam, setIsOwnedOnSteam] = useState(false);
  const [isCheckingSteam, setIsCheckingSteam] = useState(true); // Commencer en loading
  const [hasSteamConnection, setHasSteamConnection] = useState<boolean | null>(null);
  const [isTwitchConnected, setIsTwitchConnected] = useState(false);
  const [isYoutubeConnected, setIsYoutubeConnected] = useState(false);
  const [isTiktokConnected, setIsTiktokConnected] = useState(false);
  const [steamValidation, setSteamValidation] = useState<SteamValidationResult | null>(null);
  const [isValidatingSteam, setIsValidatingSteam] = useState(false);

  // Vérifier si l'utilisateur est connecté à Steam et vérifier la possession du jeu
  useEffect(() => {
    let isMounted = true;

    const checkSteam = async () => {
      // Toujours commencer en état de vérification
      setIsCheckingSteam(true);
      setIsOwnedOnSteam(false);
      setHasSteamConnection(null);

      // Attendre que le hook useGameRequestStatus ait terminé ou que le userType soit chargé
      if (requestStatusLoading || (user?.id && userType === null)) {
        return;
      }

      // Pour les creators, on vérifie Steam
      // Pour les players, pas besoin de vérifier Steam
      if (!user?.id || (userType !== 'creator' && userType !== 'player')) {
        if (isMounted) {
          setHasSteamConnection(false);
          setIsCheckingSteam(false);
        }
        return;
      }

      // Si une requête existe déjà, ne pas vérifier Steam
      if (requestStatus === 'pending' || requestStatus === 'approved' || requestStatus === 'rejected') {
        if (isMounted) {
          setHasSteamConnection(false);
          setIsCheckingSteam(false);
        }
        return;
      }

      // Si c'est un player, on n'a pas besoin de vérifier Steam
      if (userType === 'player') {
        if (isMounted) {
          setHasSteamConnection(false);
          setIsCheckingSteam(false);
        }
        return;
      }

      try {
        // Vérifier si l'utilisateur a un steam_id et les connexions sociales
        const { data: profile } = await supabase
          .from('profiles')
          .select('steam_id, twitch_connected, youtube_connected, tiktok_connected')
          .eq('id', user?.id!)
          .single();

        if (isMounted) {
          setHasSteamConnection(!!profile?.steam_id);
          setIsTwitchConnected(!!profile?.twitch_connected);
          setIsYoutubeConnected(!!profile?.youtube_connected);
          setIsTiktokConnected(!!profile?.tiktok_connected);
        }

        if (profile?.steam_id) {
          // L'utilisateur est connecté à Steam - on doit vérifier la possession ET valider le compte
          setHasSteamConnection(true);

          if (isMounted) {
            setIsValidatingSteam(true);
          }

          // Validation du compte Steam
          const validation = await validateSteamAccount(profile.steam_id);

          if (isMounted) {
            setSteamValidation(validation);
            setIsValidatingSteam(false);
          }

          if (validation.isValid) {
            // Récupérer le steam_game_id du jeu
            const { data: gameData } = await supabase
              .from('games')
              .select('steam_game_id')
              .eq('id', gameId)
              .single();

            if (gameData?.steam_game_id) {
              // Vérifier si le jeu est dans la bibliothèque Steam de l'utilisateur
              const steamData = await getPlaytimeForGame(user?.id!, gameData.steam_game_id);
              if (isMounted) {
                setIsOwnedOnSteam(steamData?.hasGame === true);
              }
            }
          }
        }

        if (isMounted) {
          setIsCheckingSteam(false);
        }
      } catch (error) {
        console.error('Error checking Steam ownership and validation:', error);
        if (isMounted) {
          setHasSteamConnection(false);
          setIsCheckingSteam(false);
          setIsValidatingSteam(false);
        }
      }
    };

    checkSteam();

    return () => {
      isMounted = false;
    };
  }, [user?.id, userType, gameId, requestStatusLoading, requestStatus]);

  // Afficher le loader tant que requestStatusLoading OU isCheckingSteam est true
  // OU si on a un user mais que son type n'est pas encore chargé
  const isLoading = requestStatusLoading || isCheckingSteam || isValidatingSteam || (user?.id && userType === null);

  if (isLoading) {
    return (
      <Button disabled className={`w-full ${className}`}>
        <Clock className="w-4 h-4 mr-2 animate-spin" />
        Loading...
      </Button>
    );
  }

  // Si les requêtes ne sont pas ouvertes
  if (!allowRequests) {
    return (
      <Button
        size="sm"
        disabled
        className={`h-8 px-2 text-xs bg-gray-500 hover:bg-gray-500 ${className}`}
      >
        <XCircle className="w-3 h-3 mr-1" />
        Requests disabled
      </Button>
    );
  }

  // Si pas de clés disponibles
  if (availableKeys === 0) {
    return (
      <Button
        size="sm"
        disabled
        className={`h-8 px-2 text-xs bg-gray-500 hover:bg-gray-500 ${className}`}
      >
        <XCircle className="w-3 h-3 mr-1" />
        No key available
      </Button>
    );
  }

  // Si une requête est déjà en cours
  if (requestStatus === 'pending') {
    return (
      <Button
        size="sm"
        disabled
        className={`h-8 px-2 text-xs bg-orange-500 hover:bg-orange-500 ${className}`}
      >
        <Clock className="w-3 h-3 mr-1" />
        Already requested
      </Button>
    );
  }

  // Si la requête a été approuvée
  if (requestStatus === 'approved') {
    return (
      <Button
        size="sm"
        disabled
        className={`h-8 px-2 text-xs bg-green-500 hover:bg-green-500 ${className}`}
      >
        <CheckCircle className="w-3 h-3 mr-1" />
        Request approved
      </Button>
    );
  }

  // Si la requête a été rejetée
  if (requestStatus === 'rejected') {
    return (
      <Button
        size="sm"
        disabled
        className={`h-8 px-2 text-xs bg-red-500 hover:bg-red-500 ${className}`}
      >
        <XCircle className="w-3 h-3 mr-1" />
        Request rejected
      </Button>
    );
  }

  // Si le jeu est déjà possédé sur Steam (pour creators uniquement)
  // IMPORTANT : vérifier aussi si on est connecté à Steam et qu'on est en train de vérifier
  // pour éviter d'afficher "Request Key" pendant la vérification
  if (userType === 'creator' || userType === 'player') {
    // Pour les players, on n'affiche pas le check Steam
    if (userType === 'player') {
      // Continuer directement au bouton Request Key pour les players
    } else if (userType === 'creator') {
      // Si on vérifie Steam et qu'on est connecté, rester en loading
      if (isCheckingSteam && hasSteamConnection === true) {
        return (
          <Button disabled className={`w-full ${className}`}>
            <Clock className="w-4 h-4 mr-2 animate-spin" />
            Loading...
          </Button>
        );
      }
      // Si le jeu est possédé, l'afficher
      if (isOwnedOnSteam) {
        return (
          <Button
            size="sm"
            disabled
            className={`h-8 px-2 text-xs bg-blue-500 hover:bg-blue-500 ${className}`}
          >
            <Package className="w-3 h-3 mr-1" />
            Game already owned
          </Button>
        );
      }
    }
  }

  // Vérifier les conditions de connexion pour les creators
  if (userType === 'creator') {
    const isSocialConnected = isTwitchConnected || isYoutubeConnected || isTiktokConnected;

    if (!hasSteamConnection || !isSocialConnected) {
      return (
        <div className={`p-2 rounded-lg bg-orange-500/10 border border-orange-500/20 text-center max-w-sm ${className}`}>
          <div className="flex items-center justify-center gap-1 mb-1 text-orange-500">
            <AlertCircle className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Connections Required</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed mb-1">
            Please connect your Steam account and at least one social media account (Twitch, YouTube, or TikTok) in your settings to request a key.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[10px] bg-background border-orange-500/30 text-orange-600 hover:bg-orange-500/5 hover:text-orange-700"
            onClick={() => navigate('/settings')}
          >
            Go to Settings
          </Button>
        </div>
      );
    }

    if (steamValidation && !steamValidation.isValid) {
      return (
        <div className={`p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-center max-w-sm ${className}`}>
          <div className="flex items-center justify-center gap-1 mb-1 text-red-500">
            <AlertCircle className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Account Requirements</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed mb-1">
            Your Steam account does not meet the minimum requirements to request a key.
          </p>
          <Button
            size="sm"
            variant="outline"
            asChild
            className="h-7 text-[10px] bg-background border-red-500/30 text-red-600 hover:bg-red-500/5 hover:text-red-700"
          >
            <Link to="/help#steam-requirements">
              <Info className="w-3 h-3 mr-1" />
              See Help
            </Link>
          </Button>
        </div>
      );
    }
  }

  // Fonction pour gérer le clic sur le bouton Request Key
  const handleRequestClick = async () => {
    // Vérifier si l'utilisateur est un créateur et connecté à Steam
    // Pour les players, on ne vérifie pas Steam
    if (userType === 'creator' && user?.id) {
      try {
        // Vérifier si l'utilisateur a un steam_id
        const { data: profile } = await supabase
          .from('profiles')
          .select('steam_id')
          .eq('id', user.id)
          .single();

        if (profile?.steam_id) {
          // Valider le compte Steam une dernière fois avant la requête
          const validation = await validateSteamAccount(profile.steam_id);
          if (!validation.isValid) {
            toast.error("Your Steam account does not meet the minimum requirements: " + validation.errors.join(", "));
            return;
          }

          // Récupérer le steam_game_id du jeu
          const { data: gameData } = await supabase
            .from('games')
            .select('steam_game_id')
            .eq('id', gameId)
            .single();

          if (gameData?.steam_game_id) {
            // Vérifier si le jeu est dans la bibliothèque Steam de l'utilisateur
            const steamData = await getPlaytimeForGame(user.id, gameData.steam_game_id);

            if (steamData?.hasGame) {
              toast.error("You already own this game on Steam. You cannot request a key for a game you already own.");
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error checking Steam ownership before request:', error);
        // En cas d'erreur, on continue quand même avec la requête
      }
    }

    // Si pas de problème, soumettre la requête normalement
    onRequestClick();
  };

  // Par défaut, afficher le bouton de requête
  return (
    <Button
      size="sm"
      onClick={handleRequestClick}
      className={`h-8 px-2 text-xs ${className}`}
    >
      <Key className="w-3 h-3 mr-1" />
      Request Key
    </Button>
  );
};
